import { memoize } from '@adv-frontend/modules-memo';
import type { AsyncFlow, AsyncFlowState } from '../../contract/async-flow';
import type { AnyGetter, GetterFactoryArgs, GetterParam, HasGetterParam } from '../lib/getterParam';
import { AsyncComputedGeneratorFlow } from './generators/instance';
import type { AsyncFlowComputationContext } from './computation';
import type { FlowAwait } from './flowAwait';

/**
 * Генератор, который возвращает вычисленное значение потока.
 *
 * Нужен, чтобы аннотировать возвращаемое значение геттера-генератора, не выписывая
 * `Generator<FlowAwait, Data, undefined>` руками.
 *
 * Потоки читают так: синхронные — через `watch`, асинхронные — через `yield*` по результату `watchAsync` и его
 * комбинаторов; `yield` любого другого значения не проходит по типам.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 *
 * @example
 * ```typescript
 * const flow = asyncComputedFlow(function* (ctx): AsyncComputedGenerator<number | string> {
 *   return ctx.watch(numberFlow);
 * });
 * ```
 */
export type AsyncComputedGenerator<Data> = Generator<FlowAwait, Data, undefined>;

/**
 * Функция-генератор, которая вычисляет значение потока и не требует параметров.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @param ctx - Контекст вычисления
 * @returns Генератор, который возвращает конечное вычисленное значение
 */
export type AsyncComputedGeneratorFlowGetter<Data> = (ctx: AsyncFlowComputationContext) => AsyncComputedGenerator<Data>;

/**
 * Функция-генератор, которая вычисляет значение потока и требует параметр.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление
 * @param ctx - Контекст вычисления
 * @param param - Значение параметра, используемое в вычислении
 * @returns Генератор, который возвращает конечное вычисленное значение
 */
export type AsyncComputedGeneratorFlowGetterWithParam<Data, Param> = (
    ctx: AsyncFlowComputationContext,
    param: Param
) => AsyncComputedGenerator<Data>;

/**
 * Объединенный тип для всех поддерживаемых функций-геттеров.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление (если есть)
 */
export type AsyncComputedFlowGetter<Data, Param> =
    | AsyncComputedGeneratorFlowGetter<Data>
    | AsyncComputedGeneratorFlowGetterWithParam<Data, Param>;

/**
 * Тип параметризованного геттера-генератора.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление
 */
export type AsyncComputedFlowGetterWithParam<Data, Param> = AsyncComputedGeneratorFlowGetterWithParam<Data, Param>;

/**
 * Ограничение публичной сигнатуры геттера асинхронного вычисляемого потока.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление
 */
export type AnyAsyncComputedFlowGetter<Data, Param> = (
    ctx: AsyncFlowComputationContext,
    param: Param
) => AsyncComputedGenerator<Data>;

/**
 * Параметры конфигурации для асинхронных вычисляемых потоков.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление (если есть)
 */
export interface AsyncComputedFlowOptions<Data, Param> {
    /**
     * Начальное состояние потока до первого завершенного вычисления.
     *
     * Используется в трех случаях:
     * - пока идет первое вычисление (нет завершенных), его `data` подставляется в
     *   `pending.data`, чтобы потребители сразу видели заглушку вместо `undefined`;
     * - если первое вычисление завершилось ошибкой, его `data` так же подставляется
     *   в `error.data`;
     * - если первое вычисление прервано через `ctx.skip()` и нет кешированного значения,
     *   поток принимает это состояние целиком.
     */
    initialValue?: AsyncFlowState<Data>;

    /**
     * Функция для сравнения вычисленных значений, чтобы определить, изменились ли они.
     *
     * Используется для предотвращения ненужных обновлений, когда вычисленное значение
     * эквивалентно предыдущему значению, даже если они не строго равны.
     *
     * @param a - Предыдущее вычисленное значение
     * @param b - Новое вычисленное значение
     * @returns `true`, если значения считаются равными, `false` в противном случае
     * @default Object.is
     *
     * @example
     * ```typescript
     * // Пользовательское сравнение для объектов
     * equals: (a, b) => deepEqual(a, b)
     * ```
     */
    equals?: (a: Data, b: Data) => boolean;

    /**
     * Функция для сравнения параметров, чтобы определить равенство ключей кеша.
     *
     * Используется для поиска существующих вычисляемых потоков в кеше, когда
     * один и тот же параметр используется несколько раз.
     *
     * @param a - Первый параметр для сравнения
     * @param b - Второй параметр для сравнения
     * @returns `true`, если параметры считаются равными, `false` в противном случае
     * @default Object.is
     *
     * @example
     * ```typescript
     * // Пользовательское сравнение для объектных параметров
     * paramEquals: (a, b) => a.id === b.id
     * ```
     */
    paramEquals?: (a: Param, b: Param) => boolean;
}

/**
 * Тип значения, которое возвращает геттер-генератор.
 *
 * @typeParam Getter - Функция вычисления
 */
type GetterData<Getter extends AnyGetter> = Getter extends AnyGetter
    ? ReturnType<Getter> extends Generator<unknown, infer Data, infer _Next>
        ? Data
        : never
    : never;

/**
 * Тип значения асинхронного вычисляемого потока.
 *
 * Если тип данных не задан явно, он выводится из возвращаемого значения геттера.
 *
 * @typeParam Data - Явно указанный тип значения
 * @typeParam Getter - Функция вычисления
 */
export type AsyncComputedFlowData<Data, Getter extends AnyGetter> = unknown extends Data ? GetterData<Getter> : Data;

/**
 * Результат создания асинхронного вычисляемого потока.
 *
 * Для геттера со вторым параметром это фабрика потоков, иначе — один поток. Для поддерживаемых форм
 * геттера условие совпадает с рантайм-проверкой арности.
 *
 * @typeParam Data - Явно указанный тип значения
 * @typeParam Param - Явно указанный тип параметра
 * @typeParam Getter - Функция вычисления
 */
export type AsyncComputedFlowResult<Data, Param, Getter extends AnyGetter> = Getter extends AnyGetter
    ? HasGetterParam<Getter> extends true
        ? (...args: GetterFactoryArgs<Getter, Param>) => AsyncFlow<AsyncComputedFlowData<Data, Getter>>
        : AsyncFlow<AsyncComputedFlowData<Data, Getter>>
    : never;

/**
 * Создает асинхронный вычисляемый поток.
 *
 * Если у геттера объявлен второй параметр, возвращается фабрика потоков, по одному на каждое
 * значение параметра, иначе — один поток. В рантайме вариант выбирается по `getter.length > 1`,
 * в типах — по списку параметров геттера.
 *
 * Значение по умолчанию у параметра задавать нельзя: у `(ctx, id = 'x') => ...` арность равна 1,
 * а в типах такой параметр неотличим от необязательного, и типы посчитают геттер
 * параметризованным. То же самое с параметром через rest-кортеж `(ctx, ...args: [id: string]) => ...`
 * и с перегруженным геттером, у которого типы видят только последнюю сигнатуру.
 *
 * Типовые аргументы указывают только для параметризованного варианта и сразу оба: явный типовой
 * аргумент отключает вывод типа геттера, и вариант без параметра выбрать уже нельзя. Тип значения
 * потока без параметра задают аннотацией возвращаемого значения геттера.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление (если есть)
 * @typeParam Getter - Функция вычисления, выводится из аргумента
 * @param getter - Функция-генератор, которая вычисляет значение
 * @param options - Необязательная конфигурация для вычисляемого потока
 * @returns Экземпляр вычисляемого потока либо фабрика таких экземпляров
 *
 * @example
 * ```typescript
 * const userNameFlow = asyncComputedFlow(function* (ctx) {
 *   const user = yield* ctx.watchAsync(userFlow);
 *   return user.name;
 * });
 * ```
 *
 * @example
 * ```typescript
 * const userNameFlow = asyncComputedFlow(function* (ctx, userId: string) {
 *   const user = yield* ctx.watchAsync(userFlow(userId));
 *   return user.name;
 * });
 *
 * // Создание экземпляров для конкретных параметров
 * const johnName = userNameFlow('john123');
 * const janeName = userNameFlow('jane456');
 * ```
 */
export function asyncComputedFlow<
    Data = unknown,
    Param = never,
    Getter extends AnyAsyncComputedFlowGetter<Data, Param> = AnyAsyncComputedFlowGetter<Data, Param>,
>(
    getter: Getter,
    options?: AsyncComputedFlowOptions<AsyncComputedFlowData<Data, Getter>, GetterParam<Getter, Param>>
): AsyncComputedFlowResult<Data, Param, Getter>;

export function asyncComputedFlow<Data = unknown, Param = never>(
    getter: AsyncComputedFlowGetterWithParam<Data, Param>,
    options?: AsyncComputedFlowOptions<Data, Param>
): AsyncFlow<Data> | ((param: Param) => AsyncFlow<Data>) {
    if (isGetterWithoutParam(getter)) {
        // Создание единичного вычисляемого потока для непараметризованных геттеров
        return withContext(() => new AsyncComputedGeneratorFlow<Data>(ctx => getter(ctx), options));
    }

    // Создание мемоизированной фабрики для параметризованных вычисляемых потоков
    return memoize(
        (param: Param) =>
            new AsyncComputedGeneratorFlow<Data>(ctx => {
                return getter(ctx, param);
            }, options),
        {
            equals: options?.paramEquals,
        }
    );
}

/**
 * Функция для определения, принимает ли функция-геттер только контекст.
 *
 * Проверяет арность (количество параметров) функции-геттера. Тип результата фабрики
 * выводится по тому же признаку — по наличию обязательного второго параметра у геттера.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление
 * @param getter - Функция-геттер для проверки
 * @returns `true`, если геттер не принимает параметров помимо контекста, `false` в противном случае
 */
function isGetterWithoutParam<Data, Param>(
    getter: AsyncComputedFlowGetterWithParam<Data, Param>
): getter is AsyncComputedGeneratorFlowGetter<Data> {
    return getter.length <= 1;
}

type LazyAsyncFlowFactory<T> = () => AsyncFlow<T>;

/**
 * Создает контекстно-зависимый прокси потока, который разделяет экземпляры потоков между запросами.
 * Это обеспечивает правильную поддержку SSR, поддерживая изолированные экземпляры потоков для каждого контекста выполнения.
 *
 * @typeParam T - Тип значения, которое производит поток
 * @param createFlow - Фабричная функция, которая создает фактический экземпляр потока
 * @returns Прокси потока, который управляет контекстно-специфичными экземплярами
 */
function withContext<T>(createFlow: LazyAsyncFlowFactory<T>): AsyncFlow<T> {
    const getFlow = memoize(createFlow);

    return {
        subscribe(listener) {
            return getFlow().subscribe(listener);
        },
        getSnapshot() {
            return getFlow().getSnapshot();
        },
        asPromise() {
            // Поток разрешается лениво и может упасть прямо здесь. Отдаем такое падение
            // реджектом: у asPromise() ждут promise, а не синхронное исключение
            try {
                return getFlow().asPromise();
            } catch (error) {
                return Promise.reject(error);
            }
        },
        getCachedSnapshot() {
            return getFlow().getCachedSnapshot();
        },
    };
}
